import { create, keyResolver, windowedFiniteBatchScheduler } from "@yornaath/batshit";
import { unpaginate, environmentContextService, taxonomyService, parameterService } from "./ddb";
import { toArray } from "./asyncIterator";

const WINDOWED_FINITE_BATCH_SCHEDULER = windowedFiniteBatchScheduler({ maxBatchSize: 5, windowMs: 1000 });

export const PROJECT_BY_ID_BATCHER = create({
  scheduler: WINDOWED_FINITE_BATCH_SCHEDULER,
  resolver: keyResolver("project_id"),
  fetcher: async (keys: string[]) => {
    const environmentContextApi = await environmentContextService();
    return toArray(
      unpaginate(
        (after) => environmentContextApi.getProjects({ projectId: keys, after }),
        ({ data }) => data.projects,
        ({ data }) => data.paging?.cursors?.after
      )
    );
  },
});

export const PROJECT_BY_NUMBER_BATCHER = create({
  scheduler: WINDOWED_FINITE_BATCH_SCHEDULER,
  resolver: keyResolver("project_number"),
  fetcher: async (keys: string[]) => {
    const environmentContextApi = await environmentContextService();
    return toArray(
      unpaginate(
        (after) => environmentContextApi.getProjects({ number: keys, after }),
        ({ data }) => data.projects,
        ({ data }) => data.paging?.cursors?.after
      )
    );
  },
});

export const PARAMETER_TYPE_BY_ID_BATCHER = create({
  scheduler: WINDOWED_FINITE_BATCH_SCHEDULER,
  resolver: keyResolver("id"),
  fetcher: async (keys: string[]) => {
    const taxonomyApi = await taxonomyService();
    return toArray(
      unpaginate(
        (after) => taxonomyApi.getAllParameterTypes({ parameterTypeId: keys, after }),
        ({ data }) => data.parameter_types,
        ({ data }) => data.paging?.cursors?.after
      )
    );
  },
});

export const PARAMETER_BY_ID_BATCHER = create({
  scheduler: WINDOWED_FINITE_BATCH_SCHEDULER,
  resolver: keyResolver("id"),
  fetcher: async (keys: string[]) => {
    const parameterApi = await parameterService();
    return toArray(
      unpaginate(
        (after) => parameterApi.getAllParameters({ parameterId: keys, after }),
        ({ data }) => data.parameters,
        ({ data }) => data.paging?.cursors?.after
      )
    );
  },
});

export const ENTRY_REVISIONS_BY_ENTRY_ID_BATCHER = create({
  scheduler: WINDOWED_FINITE_BATCH_SCHEDULER,
  resolver: keyResolver("id"),
  fetcher: async (keys: string[]) => {
    const parameterApi = await parameterService();
    return toArray(
      unpaginate(
        (after) => parameterApi.getEntryRevisions({ entryId: keys, order: "asc", after }),
        ({ data }) => data.revisions,
        ({ data }) => data.paging?.cursors?.after
      )
    );
  },
});

export const PARAMETER_BY_ASSET_ID_AND_PARAMETER_TYPE_ID_BATCHER = create({
  scheduler: WINDOWED_FINITE_BATCH_SCHEDULER,
  resolver: (parameters, key) =>
    parameters.find(({ parameter_type, parents }) => `${parents.at(0)?.id}:${parameter_type.id}` === key)!,
  fetcher: async (keys: string[]) => {
    const parameterApi = await parameterService();
    return Promise.all([
      toArray(
        unpaginate(
          (after) =>
            parameterApi.getAllParameters({
              assetId: keys.map((key) => key.split(":")[0]),
              parameterTypeId: keys.map((key) => key.split(":")[1]),
              after,
            }),
          ({ data }) => data.parameters,
          ({ data }) => data.paging?.cursors?.after
        )
      ),
      toArray(
        unpaginate(
          (after) =>
            parameterApi.getAllParameters({
              projectParameter: true,
              projectId: keys.map((key) => key.split(":")[0]),
              parameterTypeId: keys.map((key) => key.split(":")[1]),
              after,
            }),
          ({ data }) => data.parameters,
          ({ data }) => data.paging?.cursors?.after
        )
      ),
    ]).then(([parameters, projectParameters]) => [...projectParameters, ...parameters]);
  },
});

export const HIERARCHY_REFS_BY_ASSET_ID_BATCHER = create({
  scheduler: WINDOWED_FINITE_BATCH_SCHEDULER,
  resolver: keyResolver("asset_id"),
  fetcher: async (keys: string[]) => {
    const parameterApi = await parameterService();
    return parameterApi.getAssetHierarchy({ assetIds: keys }).then(({ data }) => {
      return keys.map((key, index) => ({
        asset_id: key,
        hierarchy: data.hierarchies.at(index),
      }));
    });
  },
});

export const ASSET_BY_ASSET_ID_BATCHER = create({
  scheduler: WINDOWED_FINITE_BATCH_SCHEDULER,
  resolver: keyResolver("id"),
  fetcher: async (keys: string[]) => {
    const parameterApi = await parameterService();
    return toArray(
      unpaginate(
        (after) => parameterApi.getAssets({ assetId: keys, after }),
        ({ data }) => data.assets,
        ({ data }) => data.paging?.cursors?.after
      )
    );
  },
});
